import type { ReactNode } from "react";

export type NotificationType = {
  action?: ReactNode;
  id: number;
  type: "success" | "error" | "warning" | "info" | "default";
  title: string | ReactNode;
  message?: string | ReactNode;
  priority?: boolean;
  onClose?: () => void;
  timeout?: number | false;
};

const MAX_VISIBLE_NOTIFICATIONS = 4;
const DEFAULT_TIMEOUT_BY_TYPE = {
  success: 3500,
  error: 6500,
  warning: 5000,
  info: 4500,
  default: 4500,
} satisfies Record<NotificationType["type"], number>;

function getNotificationKey(notification: NotificationType) {
  if (
    typeof notification.title !== "string" ||
    (notification.message !== undefined &&
      typeof notification.message !== "string")
  ) {
    return undefined;
  }

  return [
    notification.type,
    notification.title.trim(),
    notification.message?.trim() || "",
  ].join(":");
}

class NotificationManager {
  listNotify: NotificationType[] = [];

  callback?: (notifications: NotificationType[]) => void;

  create = (
    type: NotificationType["type"],
    notification: Omit<NotificationType, "id" | "type"> | string,
  ) => {
    const notify: NotificationType =
      typeof notification === "string"
        ? {
            title: notification,
            id: new Date().getTime() + Math.random(),
            type,
            timeout: DEFAULT_TIMEOUT_BY_TYPE[type],
          }
        : {
            ...notification,
            id: new Date().getTime() + Math.random(),
            type,
            timeout: notification.timeout ?? DEFAULT_TIMEOUT_BY_TYPE[type],
          };
    const notificationKey = getNotificationKey(notify);
    const listNotify = notificationKey
      ? this.listNotify.filter(
          (item) => getNotificationKey(item) !== notificationKey,
        )
      : [...this.listNotify];
    if (notify.priority) {
      listNotify.unshift(notify);
    } else {
      listNotify.push(notify);
    }
    this.listNotify = notify.priority
      ? listNotify.slice(0, MAX_VISIBLE_NOTIFICATIONS)
      : listNotify.slice(-MAX_VISIBLE_NOTIFICATIONS);
    this.callback?.(this.listNotify);
    return notify;
  };

  info = (notification: Omit<NotificationType, "id" | "type"> | string) =>
    this.create("info", notification);

  success = (notification: Omit<NotificationType, "id" | "type"> | string) =>
    this.create("success", notification);

  warning = (notification: Omit<NotificationType, "id" | "type"> | string) =>
    this.create("warning", notification);

  error = (notification: Omit<NotificationType, "id" | "type"> | string) =>
    this.create("error", notification);

  remove = (notification: NotificationType) => {
    this.listNotify = this.listNotify.filter((n) => notification.id !== n.id);
    this.callback?.(this.listNotify);
    notification.onClose?.();
  };
}

const notify = new NotificationManager();

export default notify;
